import { useCallback, useState } from "react";

// Pelias API Types based on the provided example response
export type PeliasFeature = {
  type: "Feature";
  geometry: {
    type: "Point";
    coordinates: [number, number]; // [longitude, latitude]
  };
  properties: {
    id: string;
    gid: string;
    layer: string;
    source: string;
    source_id: string;
    country_code: string;
    name: string;
    housenumber?: string;
    street?: string;
    postalcode?: string;
    confidence: number;
    match_type: string;
    distance?: number;
    accuracy: string;
    country: string;
    country_gid: string;
    country_a: string;
    macroregion?: string;
    macroregion_gid?: string;
    macroregion_a?: string;
    region?: string;
    region_gid?: string;
    region_a?: string;
    macrocounty?: string;
    macrocounty_gid?: string;
    county?: string;
    county_gid?: string;
    localadmin?: string;
    localadmin_gid?: string;
    locality?: string;
    locality_gid?: string;
    label: string;
  };
};

export type PeliasResponse = {
  geocoding: {
    version: string;
    attribution: string;
    query: {
      text: string;
      size: number;
      private: boolean;
      "focus.point.lat"?: number;
      "focus.point.lon"?: number;
      lang: {
        name: string;
        iso6391: string;
        iso6393: string;
        via: string;
        defaulted: boolean;
      };
      querySize: number;
      parser: string;
      parsed_text?: {
        housenumber?: string;
        street?: string;
      };
    };
    engine: {
      name: string;
      author: string;
      version: string;
    };
    timestamp: number;
  };
  type: "FeatureCollection";
  features: PeliasFeature[];
  bbox?: [number, number, number, number];
};

export type PeliasPlace = {
  placeId: string;
  description: string;
  latitude: number;
  longitude: number;
  confidence: number;
  layer: string;
};

export type PeliasPlaceDetails = {
  placeId: string;
  description: string;
  latitude: number;
  longitude: number;
  confidence: number;
  layer: string;
  fullAddress: string;
  postalcode?: string;
  country: string;
};

export type PeliasService = {
  isPeliasLoading: boolean;
  peliasPlaces: PeliasPlace[];
  getPeliasPlaces: (input: string) => void;
  getPeliasPlaceDetails: (
    placeId: string
  ) => Promise<PeliasPlaceDetails | undefined>;
};

type PeliasSearchParams = {
  text: string;
  size?: number;
  "focus.point.lat"?: number;
  "focus.point.lon"?: number;
  "boundary.country"?: string;
  layers?: string;
  lang?: string;
};

const PELIAS_API_URL = "https://maps-search.rentacar.fr/v1/autocomplete";

const buildPeliasUrl = (params: PeliasSearchParams): string => {
  const searchParams = new URLSearchParams();

  // Add required parameter
  searchParams.append("text", params.text);

  // Add optional parameters
  if (params.size !== undefined) {
    searchParams.append("size", String(params.size));
  }
  if (params["focus.point.lat"] !== undefined) {
    searchParams.append("focus.point.lat", String(params["focus.point.lat"]));
  }
  if (params["focus.point.lon"] !== undefined) {
    searchParams.append("focus.point.lon", String(params["focus.point.lon"]));
  }
  if (params["boundary.country"] !== undefined) {
    searchParams.append("boundary.country", params["boundary.country"]);
  }
  if (params.layers !== undefined) {
    searchParams.append("layers", params.layers);
  }
  if (params.lang !== undefined) {
    searchParams.append("lang", params.lang);
  }

  return `${PELIAS_API_URL}?${searchParams.toString()}`;
};

const fetchPeliasPlaces = async (input: string): Promise<PeliasResponse> => {
  const params: PeliasSearchParams = {
    text: input,
    size: 5,
    "boundary.country": "fr",
    lang: "fr-fr",
  };

  const url = buildPeliasUrl(params);
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Pelias API error: ${response.status}`);
  }

  return response.json() as Promise<PeliasResponse>;
};

const transformPeliasFeatureToPlace = (feature: PeliasFeature): PeliasPlace => {
  const [longitude, latitude] = feature.geometry.coordinates;

  return {
    placeId: feature.properties.gid,
    description: feature.properties.label,
    latitude,
    longitude,
    confidence: feature.properties.confidence,
    layer: feature.properties.layer,
  };
};

const transformPeliasFeatureToDetails = (
  feature: PeliasFeature
): PeliasPlaceDetails => {
  const [longitude, latitude] = feature.geometry.coordinates;

  return {
    placeId: feature.properties.gid,
    description: feature.properties.name,
    latitude,
    longitude,
    confidence: feature.properties.confidence,
    layer: feature.properties.layer,
    fullAddress: feature.properties.label,
    postalcode: feature.properties.postalcode,
    country: feature.properties.country,
  };
};

export default function usePeliasPlaces(): PeliasService {
  const [peliasPlaces, setPeliasPlaces] = useState<PeliasPlace[]>([]);
  const [isPeliasLoading, setIsPeliasLoading] = useState<boolean>(false);

  const getPeliasPlaces = useCallback(async (input: string) => {
    if (input.length < 3) {
      setPeliasPlaces([]);

      return;
    }

    setIsPeliasLoading(true);

    try {
      const response = await fetchPeliasPlaces(input);
      const places = response.features.map(transformPeliasFeatureToPlace);
      setPeliasPlaces(places);
    } catch (error) {
      console.error("Error fetching Pelias places:", error);
      setPeliasPlaces([]);
    } finally {
      setIsPeliasLoading(false);
    }
  }, []);

  const getPeliasPlaceDetails = useCallback(
    async (placeId: string): Promise<PeliasPlaceDetails | undefined> => {
      try {
        // For Pelias, we can search by the gid to get detailed information
        const params: PeliasSearchParams = {
          text: placeId,
          size: 1,
          "boundary.country": "FR",
          lang: "fr",
        };

        const url = buildPeliasUrl(params);
        const response = await fetch(url);

        if (!response.ok) {
          throw new Error(`Pelias details API error: ${response.status}`);
        }

        const data = (await response.json()) as PeliasResponse;

        if (data.features.length > 0) {
          return transformPeliasFeatureToDetails(data.features[0]);
        }

        return undefined;
      } catch (error) {
        console.error("Error fetching Pelias place details:", error);

        return undefined;
      }
    },
    []
  );

  return {
    isPeliasLoading,
    peliasPlaces,
    getPeliasPlaces,
    getPeliasPlaceDetails,
  };
}
